import { Link } from 'react-router-dom';

const faqs = [

    {
        question: "What is competitive high school esports?",
        answer: "Esports is organized, varsity-level competitive video gaming sanctioned through the Michigan High School Esports Federation (MiHSEF). Just like traditional athletics, student-athletes practice, analyze game footage (VODs), develop communication and teamwork, and compete against other high schools across Michigan.",
    },
    {
        question: "Is this just 'playing video games'?",
        answer: "No. High school esports requires structured dedication, sportsmanship, and strategic thinking—just like traditional sports. Students learn leadership, graceful resilience in victory and defeat, digital citizenship, and time management. In addition, our new broadcasting crew develops real-world skills in audio engineering, live camera switching, public speaking, and digital video production.",
    },
    {
        question: "Are the games and streaming environments safe for my child?",
        answer: "Yes. We only play school-approved titles rated E or E10+. In-game voice chat with strangers and all microtransactions are disabled during school events. All Discord communication and video broadcasts are strictly monitored by Coach Anklam and comply with District Acceptable Use Policies (AUPs) and student privacy guidelines (FERPA).",
    },
    {
        question: "How much does it cost to participate?",
        answer: "Participation is 100% free with no registration fees or athletic dues. All lab gaming PCs, tournament consoles, monitors, and broadcasting gear are provided by the school. Optional custom team jerseys and special travel trips (such as State Finals or college campus tours) may have additional costs subsidized through team fundraising efforts.",
    },
    {
        question: "How can parents watch and support their student?",
        answer: "Matches are live-streamed online with live commentary produced by our student broadcast crew, and parents are always welcome to join us in the High School Media Center to watch matches in person!",
    },
    {
        question: "Can my student participate if they play other sports (Football, Cheer, Cross Country)?",
        answer: "Yes! Dual-sport participation is actively supported. Coach Anklam coordinates directly with athletic coaches so students can participate without schedule conflicts.",
    },
];

export default function ParentsGuidePage() {
    return (
        <div className="relative overflow-hidden">
            {/* Hero Section */}
            <section className="relative py-14 bg-gradient-to-b from-blue-950/40 via-secondary-900 to-secondary-900 border-b border-secondary-800" aria-labelledby="parents-hero-title">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#3B82F6_0%,_transparent_50%)] opacity-10" aria-hidden="true" />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center animate-fade-in">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-500/20 border border-blue-500/40 rounded-2xl mb-4 shadow-lg" aria-hidden="true">
                            <span className="text-3xl">👨‍👩‍👧</span>
                        </div>
                        <div className="inline-block bg-blue-950 text-blue-300 border border-blue-500/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                            Fall 2026 Parent Resource
                        </div>
                        <h1 id="parents-hero-title" className="text-3xl sm:text-4xl md:text-5xl font-display font-extrabold text-white mb-3">
                            Parents <span className="text-blue-400">Guide</span>
                        </h1>
                        <p className="text-base sm:text-lg text-secondary-200 max-w-2xl mx-auto leading-relaxed">
                            Everything you need to know about Peck Pirates Esports and our new Broadcasting & Production Crew — explained clearly with zero jargon.
                        </p>
                    </div>
                </div>
            </section>

            {/* Welcome & Fall 2026 Info Meeting Callout */}
            <section className="py-10 bg-secondary-900" aria-label="Meeting announcement and overview">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
                    {/* Meeting Notice */}
                    <div className="card border-2 border-primary-500/50 bg-gradient-to-r from-primary-950/60 to-secondary-900 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div>
                            <div className="text-xs font-bold text-yellow-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                <span aria-hidden="true">📣</span> Parent & Student Information Meeting
                            </div>
                            <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
                                Tuesday, Sept 1, 2026 @ High School Media Center
                            </h2>
                            <p className="text-secondary-200 text-xs sm:text-sm">
                                <strong>3:00 – 5:00 PM:</strong> Drop-in Open Lab (try the gear & meet the coach) • <strong>4:30 PM:</strong> Brief Presentation.
                            </p>
                        </div>
                        <a
                            href="https://forms.gle/2GvpB888G5xRci1S9"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Online Google Signup Form (opens in a new tab)"
                            className="bg-yellow-400 hover:bg-yellow-300 text-primary-950 font-bold px-4 py-2.5 rounded-lg text-xs transition-colors shrink-0 shadow-md flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:outline-none"
                        >
                            <span>Sign Up Form</span>
                            <span aria-hidden="true">↗</span>
                        </a>
                    </div>

                    {/* Welcome Card */}
                    <div className="card border-blue-500/30 bg-gradient-to-br from-blue-950/30 to-secondary-900 p-6">
                        <h2 className="text-xl font-display font-bold text-blue-400 mb-3 flex items-center gap-2">
                            <span aria-hidden="true">👋</span> Welcome to Peck Pirates Esports!
                        </h2>
                        <p className="text-secondary-200 text-sm leading-relaxed mb-3">
                            We know competitive gaming and digital broadcasting can seem unfamiliar if you didn't grow up with them—and that's completely okay! This program is designed as a school-sponsored activity that reinforces academic excellence, sportsmanship, and real-world career skills.
                        </p>
                        <p className="text-secondary-200 text-sm leading-relaxed">
                            Whether your student is competing in matches or operating the camera and audio mixer behind the scenes, you are an essential part of their journey. Let's make cheering for your student easy and fun! 🎉
                        </p>
                    </div>
                </div>
            </section>

            {/* Competitive Game Focus: Super Smash Bros. Ultimate */}
            <section className="py-10 bg-secondary-950 border-t border-secondary-800" aria-labelledby="competitive-game-title">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <span className="text-xs font-bold uppercase tracking-wider text-primary-400 bg-primary-950/80 px-3 py-1 rounded-full border border-primary-500/30 inline-block mb-2">
                            Fall 2026 Competitive Title
                        </span>
                        <h2 id="competitive-game-title" className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
                            🎮 Competitive Title: Super Smash Bros. Ultimate
                        </h2>
                        <p className="text-secondary-300 text-sm max-w-xl mx-auto">
                            Sanctioned by the Michigan High School Esports Federation (MiHSEF)
                        </p>
                    </div>

                    <div className="card border-2 border-secondary-700 bg-secondary-900 p-6 sm:p-8">
                        <div className="grid md:grid-cols-3 gap-6 items-center">
                            <div className="h-44 rounded-xl overflow-hidden bg-secondary-800 border border-secondary-700">
                                <img
                                    src="/images/games/smash-bros.png"
                                    alt="Super Smash Bros. Ultimate official game graphic"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="md:col-span-2 space-y-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <h3 className="text-xl font-display font-bold text-white">Super Smash Bros. Ultimate</h3>
                                    <span className="text-xs bg-green-500/20 text-green-300 border border-green-500/30 px-2.5 py-1 rounded-full font-semibold">
                                        ESRB: E10+ (Everyone 10+)
                                    </span>
                                </div>
                                <p className="text-secondary-200 text-sm leading-relaxed">
                                    A fast-paced platform fighting game where characters attempt to knock opponents off the stage. Unlike violent combat games, Smash Bros is cartoon-styled with zero graphic violence.
                                </p>

                                <div className="grid sm:grid-cols-2 gap-3 pt-2 text-xs">
                                    <div className="bg-secondary-800 p-3 rounded-lg border border-secondary-700">
                                        <strong className="text-white block mb-0.5">⚔️ Solos (1v1)</strong>
                                        <span className="text-secondary-300">Individual head-to-head match where players pick characters based on tactical matchups.</span>
                                    </div>
                                    <div className="bg-secondary-800 p-3 rounded-lg border border-secondary-700">
                                        <strong className="text-white block mb-0.5">🛡️ Crews (3v3 Team)</strong>
                                        <span className="text-secondary-300">Relay format where teammates share lives. When one player falls, the next teammate enters the arena!</span>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <Link
                                        to="/parents/smash-bros"
                                        className="text-primary-400 hover:text-primary-300 font-semibold text-sm inline-flex items-center gap-1 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none rounded"
                                    >
                                        <span>Read Full Smash Bros Spectator Guide for Parents</span>
                                        <span aria-hidden="true">→</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* NEW SECTION: Broadcasting, Production & Social Media Guide */}
            <section className="py-12 bg-secondary-900 border-t border-secondary-800" aria-labelledby="broadcasting-guide-title">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <div className="inline-block bg-yellow-400/10 text-yellow-300 border border-yellow-400/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                            New for Fall 2026
                        </div>
                        <h2 id="broadcasting-guide-title" className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
                            🎬 Production, Broadcasting & Social Media Crew
                        </h2>
                        <p className="text-secondary-200 text-sm max-w-2xl mx-auto leading-relaxed">
                            Think of this as high school drama club meets live sports television! Non-competitor roles give students hands-on experience with professional media equipment and digital communication.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                        {/* Casters */}
                        <div className="card p-5 bg-secondary-800 border-secondary-700">
                            <div className="w-10 h-10 bg-yellow-400/20 rounded-xl flex items-center justify-center text-xl mb-3" aria-hidden="true">
                                🎙️
                            </div>
                            <h3 className="text-base font-bold text-white mb-1">On-Air Casters & Commentators</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed mb-2">
                                Students call the matches live on microphone. <strong>Play-by-play</strong> casters describe the immediate action, while <strong>color commentators</strong> analyze player decisions and statistics.
                            </p>
                            <span className="text-xs text-yellow-400 font-semibold block">Skills: Public speaking, poise, vocal clarity, quick thinking</span>
                        </div>

                        {/* Stream Tech */}
                        <div className="card p-5 bg-secondary-800 border-secondary-700">
                            <div className="w-10 h-10 bg-blue-400/20 rounded-xl flex items-center justify-center text-xl mb-3" aria-hidden="true">
                                🎛️
                            </div>
                            <h3 className="text-base font-bold text-white mb-1">Stream Tech & Broadcast Engineers</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed mb-2">
                                Students manage Open Broadcaster Software (OBS), switch between player game cameras, balance microphone audio levels, and troubleshoot hardware.
                            </p>
                            <span className="text-xs text-blue-400 font-semibold block">Skills: Audio mixing, video routing, hardware troubleshooting</span>
                        </div>

                        {/* Graphics */}
                        <div className="card p-5 bg-secondary-800 border-secondary-700">
                            <div className="w-10 h-10 bg-purple-400/20 rounded-xl flex items-center justify-center text-xl mb-3" aria-hidden="true">
                                🎨
                            </div>
                            <h3 className="text-base font-bold text-white mb-1">Graphics & Broadcast Designers</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed mb-2">
                                Students create live scoreboards, broadcast lower-thirds, player profile cards, and tournament bracket graphics using digital design tools.
                            </p>
                            <span className="text-xs text-purple-400 font-semibold block">Skills: Graphic design, visual communication, animation</span>
                        </div>

                        {/* Social Media */}
                        <div className="card p-5 bg-secondary-800 border-secondary-700">
                            <div className="w-10 h-10 bg-green-400/20 rounded-xl flex items-center justify-center text-xl mb-3" aria-hidden="true">
                                📱
                            </div>
                            <h3 className="text-base font-bold text-white mb-1">Social Media & Highlight Editors</h3>
                            <p className="text-secondary-300 text-xs leading-relaxed mb-2">
                                Students clip exciting match moments, edit short highlight reels for YouTube and school channels, and help publish match updates under school supervision.
                            </p>
                            <span className="text-xs text-green-400 font-semibold block">Skills: Video editing, social storytelling, responsible media management</span>
                        </div>
                    </div>

                    {/* Safety, Moderation & FERPA Box */}
                    <div className="card border-2 border-yellow-500/40 bg-gradient-to-br from-yellow-950/30 to-secondary-800 p-5">
                        <h3 className="text-sm font-bold text-yellow-300 mb-2 flex items-center gap-2">
                            <span aria-hidden="true">🔒</span> Parent Safety & Student Privacy Commitment
                        </h3>
                        <ul className="text-xs text-secondary-200 space-y-1.5 leading-relaxed">
                            <li className="flex items-start gap-2">
                                <span className="text-yellow-400 font-bold" aria-hidden="true">•</span>
                                <span><strong>School Supervision:</strong> All broadcast content and social media output is monitored directly by Coach Anklam.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-yellow-400 font-bold" aria-hidden="true">•</span>
                                <span><strong>Privacy & FERPA Protection:</strong> Student full names or personal details are not broadcast unless authorized by parental consent. Directory opt-out requests are strictly honored.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-yellow-400 font-bold" aria-hidden="true">•</span>
                                <span><strong>District Policy Compliance:</strong> Broadcasting adheres directly to Peck Community Schools Board Policy po5722 (Student Expression) and po7544 (Social Media).</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-12 bg-secondary-950 border-t border-secondary-800" aria-labelledby="faq-section-title">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 id="faq-section-title" className="text-2xl font-display font-bold text-white mb-8 text-center flex items-center justify-center gap-2">
                        <span aria-hidden="true">❓</span> Frequently Asked Questions for Parents
                    </h2>

                    <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                            <div key={idx} className="card p-5 bg-secondary-900 border-secondary-700">
                                <h3 className="text-base font-bold text-white mb-1.5">{faq.question}</h3>
                                <p className="text-secondary-300 text-xs sm:text-sm leading-relaxed">{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Coach Contact */}
            <section className="py-10 bg-secondary-900 border-t border-secondary-800" aria-labelledby="contact-section-title">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 id="contact-section-title" className="text-xl font-display font-bold text-white mb-2">Still Have Questions?</h2>
                    <p className="text-secondary-300 text-xs sm:text-sm mb-4 max-w-lg mx-auto">
                        We are here to support you and your student. Feel free to contact Coach Brian Anklam anytime.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4 text-xs sm:text-sm">
                        <a
                            href="mailto:anklamb@peckschools.net"
                            className="text-primary-400 hover:text-primary-300 font-medium underline focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none rounded px-1"
                        >
                            anklamb@peckschools.net
                        </a>
                        <span className="text-secondary-600" aria-hidden="true">•</span>
                        <a
                            href="tel:8103780645"
                            className="text-primary-400 hover:text-primary-300 font-medium underline focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none rounded px-1"
                        >
                            (810) 378-0645
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}

